/**
 * SET UP THE ACCOUNTS, ONE AT A TIME (a tester's report, 2026-10-21: "Recognise existing Chart of Accounts entries
 * automatically, create only genuinely missing accounts, and ask the user to select the appropriate accounting element
 * from a predefined list instead of repeatedly asking open-ended classification questions").
 *
 * The client's headings that are already accounts in their chart never reach this screen: the engine posts them to the
 * existing account. What is left is each account Leveri needs a class for — new ones from the client's headings, and
 * accounts of the previous trial balance — shown one at a time: its name, whether it is new, the five elements (Asset,
 * Liability, Equity, Income, Expense), then the kind within the element, Leveri's proposal chosen in advance. "Save and
 * next" is the governed review act on that account's decision, in the person's name; after the last, the accounts are
 * prepared again with them. Nothing is decided here: the choices, the proposal and the elements are the server's.
 */
import { useMemo, useState } from "react";
import { WorkActionResponse, type ConclusionRow, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading, Select } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";
import { AccountPicker } from "./accountPicker.js";

const ELEMENTS = ["Asset", "Liability", "Equity", "Income", "Expense"] as const;

/** The accounts left to set up: current mapping decisions about a named account, this viewer may review, not yet reviewed. */
export function accountsToSetUp(work: WorkFile): ConclusionRow[] {
  return work.conclusions.filter((c) => c.current && c.questionKind === "ACCOUNT_MAPPING" && c.accountSetup && c.canReview && !c.review.act);
}

export function AccountSetup({ work, onWork, onOutcome }: { work: WorkFile; onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void }) {
  const left = useMemo(() => accountsToSetUp(work), [work]);
  const setUp = useMemo(() => work.conclusions.filter((c) => c.current && c.questionKind === "ACCOUNT_MAPPING" && c.accountSetup && c.review.act).length, [work.conclusions]);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(() => new Set());
  const queue = useMemo(() => [...left.filter((c) => !skipped.has(c.id)), ...left.filter((c) => skipped.has(c.id))], [left, skipped]);
  const step = queue[0] ?? null;
  const total = setUp + left.length;
  // How many there were when the person began: after the last, the accounts are prepared again and the new version
  // asks nothing, so the count would otherwise read nothing was ever set up.
  const [began] = useState(total);

  if (!step) {
    const shown = Math.max(total, began);
    return (
      <div data-testid="account-setup-done">
        <p className="text-[15px] text-ink">{shown > 0 ? `All ${shown} account${shown === 1 ? " is" : "s are"} set up.` : "No account is waiting to be set up."}</p>
        {work.preparation?.prepareAgain.sentence && <p className="mt-2 text-[13px] text-stone">{work.preparation.prepareAgain.sentence}</p>}
      </div>
    );
  }
  return (
    <div data-testid="account-setup">
      <p className="text-[13px] text-stone [font-variant-numeric:tabular-nums]" data-testid="account-setup-progress">
        Account {setUp + 1} of {total}{setUp > 0 ? ` · ${setUp} set up` : ""}
      </p>
      <div className="mt-2 flex gap-1" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => <span key={i} className={`h-1 flex-1 ${i < setUp ? "bg-forest" : i === setUp ? "bg-forest-tint" : "bg-rule"}`} />)}
      </div>
      <SetUpOne key={step.id} work={work} row={step} last={left.length === 1}
        onSkip={left.length > 1 ? () => setSkipped((s) => new Set([...s, step.id])) : null}
        onWork={onWork} onOutcome={onOutcome} />
    </div>
  );
}

function SetUpOne({ work, row, last, onSkip, onWork, onOutcome }: {
  work: WorkFile; row: ConclusionRow; last: boolean; onSkip: (() => void) | null;
  onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void;
}) {
  const setup = row.accountSetup!;
  const options = row.adjustOptions.filter((o) => o.element);
  const proposed = options.find((o) => o.conclusionKind === row.conclusionKind) ?? null;
  const elements = ELEMENTS.filter((e) => options.some((o) => o.element === e));
  const [element, setElement] = useState<string | null>(proposed?.element ?? null);
  const [kind, setKind] = useState<string>(proposed?.conclusionKind ?? "");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();
  const within = options.filter((o) => o.element === element);
  const chosen = within.find((o) => o.conclusionKind === kind) ?? null;

  function pick(e: string) {
    setElement(e);
    const only = options.filter((o) => o.element === e);
    // Leveri's proposal stays chosen when it is of this element; a single kind is chosen for the person.
    setKind(proposed?.element === e ? proposed.conclusionKind : only.length === 1 ? only[0]!.conclusionKind : "");
  }

  async function save() {
    if (busy || !chosen || !work.review.versionId) return;
    setBusy(true); setFailure(null);
    try {
      const accepted = chosen.conclusionKind === row.conclusionKind;
      const result = await call("POST", `/api/work/${work.id}/conclusions/${row.id}/review`, WorkActionResponse, {
        artifactVersionId: work.review.versionId,
        outcome: accepted ? "ACCEPTED" : "ADJUSTED",
        basis: `Set up as ${chosen.element} — ${chosen.label}, in the account-by-account setup.`,
        ...(accepted ? {} : { adjustedConclusion: { conclusionKind: chosen.conclusionKind, conclusionText: chosen.label } }),
        submissionKey: keyFor(`setup:${row.id}:${work.review.versionId}:${chosen.conclusionKind}`),
      });
      let next = result.work;
      let outcome = `“${setup.name}” set up as ${chosen.element} — ${chosen.label}.`;
      // The last account set up: the accounts are prepared again with them, where the work has settled.
      if (last && next.preparation?.prepareAgain.available) {
        const again = await call("POST", `/api/work/${work.id}/prepare-again`, WorkActionResponse);
        next = again.work;
        outcome = `Every account is set up. ${again.outcome}`;
      } else if (last && next.preparation?.prepareAgain.sentence) {
        outcome = `Every account is set up. ${next.preparation.prepareAgain.sentence}`;
      }
      onOutcome(outcome);
      onWork(next);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-5" data-testid="account-setup-step">
      <h3 className="text-[18px] font-medium text-ink">{setup.name}</h3>
      <p className="mt-1 text-[13px] text-stone">
        {setup.isNew ? "A new account, from the client's own headings. Leveri sets it up with the type you choose." : "An account in the client's chart (the previous trial balance)."}
      </p>
      {proposed && <p className="mt-2 text-[13px] text-ink-soft">Leveri proposes {proposed.element} — {proposed.label}, from its name.</p>}

      <p className="mt-5 text-[13px] font-medium text-ink">What kind of account is it?</p>
      <div role="radiogroup" aria-label="Accounting element" className="mt-2 flex flex-wrap gap-2">
        {elements.map((e) => (
          <button key={e} type="button" role="radio" aria-checked={element === e} disabled={busy} onClick={() => pick(e)} data-testid={`setup-element-${e.toLowerCase()}`}
            className={`border px-3.5 py-2 text-[14px] ${element === e ? "border-forest bg-forest-tint text-ink" : "border-rule text-ink-soft hover:bg-band"}`}>
            {e}
          </button>
        ))}
      </div>
      {element && within.length > 1 && (
        <div className="mt-4 max-w-[360px]">
          <Select label={`Which kind of ${element.toLowerCase()}`} value={kind} disabled={busy} onChange={(e) => setKind(e.target.value)} data-testid="setup-kind">
            <option value="">Choose</option>
            {within.map((o) => <option key={o.conclusionKind} value={o.conclusionKind}>{o.label}</option>)}
          </Select>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3.5">
        <Button disabled={busy || !chosen} onClick={() => void save()} data-testid="setup-save">{last ? "Save and finish" : "Save and next"}</Button>
        {onSkip && <button type="button" className="text-[13px] text-stone hover:text-ink" disabled={busy} onClick={onSkip}>Skip for now</button>}
      </div>
      {/* A heading that IS an account of the chart under another name: posted there, never set up a second time. */}
      {setup.isNew && row.accountTarget?.kind === "HEAD" && work.preparation && (
        <div className="mt-4 border-t border-rule-soft pt-3">
          <AccountPicker workId={work.id} target={row.accountTarget} chart={work.preparation.chart} accountTypes={work.preparation.accountTypes}
            label="It is an account already in the chart" about={`everything under “${setup.name}”`} onWork={onWork} onOutcome={onOutcome} />
        </div>
      )}
      {busy && <div className="mt-3"><Reading>Saving…</Reading></div>}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </div>
  );
}
